import { useRef, useState, useCallback } from "react";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import { Plus, Edit, Trash2, Upload, Download, AlertTriangle, CheckCircle, Info, Lock} from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import * as XLSX from "xlsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import { createBankAccountService, deleteBankAccountService, updateBankAccountService, bulkImportBankAccountsService, addLoanType, updateLoanType, deleteLoanType} from "@/services/adminService";
import { validateIFSC, validateAccountNumber } from "@/lib/validations";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const PreviewCellInput = ({
  value,
  onChange,
  error,
  warning,
  type = "text",
  id,
  className,
  placeholder = "",
}) => {
  const baseClasses =
    "h-8 text-xs px-2 py-0 border w-full bg-background transition-colors focus-visible:ring-1 focus-visible:ring-primary";

  // Apply conditional Tailwind classes for visual feedback
  const stateClass = error
    ? "border-red-400 focus-visible:ring-red-400 bg-red-50 dark:bg-red-900/30"
    : warning
    ? "border-amber-400 focus-visible:ring-amber-400 bg-amber-50 dark:bg-amber-900/20"
    : "border-input";

  return (
    <div className="flex flex-col gap-0.5">
      <Input
        type={type}
        id={id} // Unique ID for focus restoration
        value={value}
        onChange={onChange}
        className={`${baseClasses} ${stateClass} ${className}`}
        placeholder={placeholder}
      />
      {/* Visual error/warning feedback below the input */}
      {error && <p className="text-[10px] text-red-500 font-medium">{error}</p>}
      {!error && warning && (
        <p className="text-[10px] text-amber-600 font-medium">{warning}</p>
      )}
    </div>
  );
};

// =================================================================================
// REFACTORED COMPONENT: PreviewCellSelect (Uses ShadCN Select)
// =================================================================================

const PreviewCellSelect = ({
  value,
  onChange,
  error,
  id,
  options = [],
}) => {
  const isInvalid = !!error;
  
  // Custom class for the trigger to match the inline table style
  const selectTriggerClass = `h-8 text-xs border focus:ring-1 focus:ring-primary w-full ${
    isInvalid
      ? "border-red-400 bg-red-50 dark:bg-red-900/30"
      : "border-input"
  }`;

  return (
    <div className="flex flex-col gap-0.5">
      <Select
        value={value}
        onValueChange={onChange}
      >
        <SelectTrigger id={id} className={selectTriggerClass}>
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isInvalid && (
        <p className="text-[10px] text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
};


const BankAccountsTab = ({
  bankAccounts,
  setBankAccounts,
  companyLoanTypes,
  setAllLoanTypes,
}) => {
  // --- Existing State Declarations ---
  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [openLoanModal, setOpenLoanModal] = useState(false);
  const [editingLoanIndex, setEditingLoanIndex] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });
  const { reloadOnboardingStatus } = useOnboarding();
  const BANK_OPTIONS = [
    "HDFC Bank",
    "ICICI Bank",
    "IDFC Bank",
    "Kotak Mahindra Bank",
    "Citi Bank",
    "Axis Bank",
    "State Bank of India",
    "IndusInd Bank",
    "Yes Bank",
    "Bank of Baroda",
    "Other",
  ];

  const emptyForm = {
    account_name: "",
    bank_name: "",
    account_number: "",
    ifsc: "",
    account_code: "",
    account_type: "current",
    is_default: false,
  };

  const [form, setForm] = useState(emptyForm);
  const [loanForm, setLoanForm] = useState({ loan_type: "", interest_rate: 0 });
  const { hasFeature, isPaidOnline } = useSubscription();
  // --- Import State and Refs ---
  const importInputRef = useRef(null);
  const focusRef = useRef(null); // Ref to store the ID of the element to focus
  // ⭐ FIX: Ref to store the cursor position
  const cursorPositionRef = useRef({ start: 0, end: 0 });
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importStats, setImportStats] = useState({
    total: 0,
    valid: 0,
    withWarnings: 0,
    withErrors: 0,
  });
  const navigate = useNavigate();
  const hasMultiBankAccounts = hasFeature("multi_bank_accounts");

  const isBankLimitReached = !hasMultiBankAccounts && bankAccounts.length >= 1;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const { can } = usePermission();
  const { canView, canEdit, canCreate, canDelete } = createCanHelpers(can);
  
  const canViewBankConfig = canView("settings_bank_configuration");
  const canEditBankConfig = canEdit("settings_bank_configuration");
  const canCreateBankConfig = canCreate("settings_bank_configuration");
  const canDeleteBankConfig = canDelete("settings_bank_configuration");
  const canViewLoanConfig = canView("settings_loan_configuration");
  const canEditLoanConfig = canEdit("settings_loan_configuration");
  const canCreateLoanConfig = canCreate("settings_loan_configuration");
  const canDeleteLoanConfig = canDelete("settings_loan_configuration");
  
  // --- Utility Functions (unchanged logic) ---
  const parseBool = (v) => { 
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return ["1", "true", "yes", "y"].includes(s);
    }
    return false;
  };

  const normalizeAccountType = (v) => {
    const s = (v ?? "").toString().trim().toLowerCase();
    if (!s) return "current";
    if (["current", "current account"].includes(s)) return "current";
    if (["savings", "saving", "savings account"].includes(s)) return "savings";
    return s;
  };

  const summarizeImport = (rows) => {
    let total = rows.length;
    let valid = 0;
    let withWarnings = 0;
    let withErrors = 0;

    rows.forEach((r) => {
      if (r.hasError) withErrors++;
      else if (r.hasWarning) {
        withWarnings++;
        valid++;
      } else {
        valid++;
      }
    });

    return { total, valid, withWarnings, withErrors };
  };

  const validateImportRowBase = (row) => {
    const errors = [];
    const warnings = [];
    const fieldErrors = {};

    // Required
    if (!row.account_name?.trim()) {
      errors.push("Account Name is required");
      fieldErrors.account_name = "required";
    }
    if (!row.bank_name?.trim()) {
      errors.push("Bank Name is required");
      fieldErrors.bank_name = "required";
    }
    if (!row.account_number?.trim()) {
      errors.push("Account Number is required");
      fieldErrors.account_number = "required";
    } else if (!validateAccountNumber(row.account_number)) {
      errors.push("Account Number must be 9–18 digits");
      fieldErrors.account_number = "invalid";
    }

    // Optional IFSC, but must be valid if present
    if (row.ifsc && row.ifsc.trim()) {
      if (!validateIFSC(row.ifsc.trim().toUpperCase())) {
        errors.push("Invalid IFSC format (ABCD0XXXXXX)");
        fieldErrors.ifsc = "invalid";
      }
    }

    // Account type validation
    const t = (row.account_type || "").toString().trim().toLowerCase();
    if (!["current", "savings"].includes(t)) {
      errors.push("Account Type must be 'current' or 'savings'");
      fieldErrors.account_type = "invalid";
    }

    // Optional: account_code warning if too long
    if (row.account_code && row.account_code.toString().length > 30) {
      warnings.push("Account Code seems long (check if correct)");
      fieldErrors.account_code = "warning";
    }

    const hasError = errors.length > 0;
    const hasWarning = warnings.length > 0;

    return {
      ...row,
      errors,
      warnings,
      fieldErrors,
      hasError,
      hasWarning,
      selected: hasError ? false : row.selected ?? true,
    };
  };

  const validateImportRows = (rows, existingAccountNosSet) => {
    // 1) base validation
    let next = rows.map(validateImportRowBase);

    // 2) duplicates against existing + within import
    const map = new Map(); // accountNo -> indices[]
    next.forEach((r, i) => {
      const no = (r.account_number || "").trim();
      if (!no) return;
      if (!map.has(no)) map.set(no, []);
      map.get(no).push(i);
    });

    const addError = (idx, message, field) => {
      const r = next[idx];
      const fieldErrors = { ...(r.fieldErrors || {}) };
      if (field) fieldErrors[field] = fieldErrors[field] || "invalid";
      const errors = Array.isArray(r.errors) ? [...r.errors] : [];
      if (!errors.includes(message)) errors.push(message);
      next[idx] = {
        ...r,
        errors,
        fieldErrors,
      };
    };

    for (const [no, idxs] of map.entries()) {
      if (idxs.length > 1) {
        idxs.forEach((idx) =>
          addError(idx, "Duplicate Account Number found in import file", "account_number")
        );
      }
      if (existingAccountNosSet.has(no)) {
        idxs.forEach((idx) =>
          addError(idx, "Account Number already exists in system", "account_number")
        );
      }
    }

    // 3) recompute status flags + selection
    next = next.map((r) => {
      const hasError = (r.errors || []).length > 0;
      const hasWarning = (r.warnings || []).length > 0;
      return {
        ...r,
        hasError,
        hasWarning,
        selected: hasError ? false : r.selected ?? true,
      };
    });

    return next;
  };

  // --- Handlers ---

  const handleDownloadBankTemplate = () => {
    const link = document.createElement("a");
    link.href = "/excel-templates/bank_accounts_template.xlsx";
    link.download = "bank_accounts_template.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadSpecificBankTemplate = async (bankName) => {
    try {
      const bankKey = bankName.toLowerCase().replace(/\s+/g, "_");
      let templateUrl;
      try {
        const module = await import( `../../../assets/bank-templates/${bankKey}.xlsx` );
        templateUrl = module.default;
      } catch (e) {
        templateUrl = `${process.env.REACT_APP_ASSETS_URL || ""}/bank-templates/${bankKey}.xlsx`;
      }
      
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = `${bankKey}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Template not available for " + bankName);
    }
  };

  const handleBankImport = async (e) => {
    if(!canCreate("settings_bank_configuration", true)) return;
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const existingAccountNos = new Set(
        (bankAccounts || [])
          .map((b) => (b.account_number || "").toString().trim())
          .filter(Boolean)
      );

      const cleanRows = rows.map((r, idx) => {
        // ... header parsing logic ...
        const account_name =
          (r["Account Name *"] ?? r["account_name"] ?? r["AccountName"] ?? r["accountName"] ?? "")
            .toString()
            .trim();

        const bank_name =
          (r["Bank Name *"] ?? r["bank_name"] ?? r["BankName"] ?? r["bankName"] ?? "")
            .toString()
            .trim();

        let account_number =
          (r["Account Number *"] ??
            r["account_number"] ??
            r["AccountNo"] ??
            r["accountNo"] ??
            "")
            .toString()
            .trim();

        // sanitize: digits only
        account_number = account_number.replace(/\D/g, "");

        const ifsc = (r["IFSC"] ?? r["ifsc"] ?? "").toString().trim().toUpperCase();
        const account_code =
          (r["Account Code"] ?? r["account_code"] ?? r["AccountCode"] ?? r["accountCode"] ?? "")
            .toString()
            .trim();

        const account_type = normalizeAccountType(
          r["Account Type *"] ?? r["account_type"] ?? r["AccountType"] ?? r["accountType"]
        );

        const is_default = parseBool(
          r["Is Default"] ?? r["is_default"] ?? r["IsDefault"] ?? r["isDefault"]
        );

        return {
          _rowIndex: idx + 2,
          account_name,
          bank_name,
          account_number,
          ifsc,
          account_code,
          account_type,
          is_default,
          selected: true,
        };
      });

      const validated = validateImportRows(cleanRows, existingAccountNos);
      setImportPreview(validated);
      setImportStats(summarizeImport(validated));
      setShowImportPreview(true);

      toast.info("Preview loaded. Review before importing.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to import bank accounts");
    } finally {
      e.target.value = null; // allow re-uploading same file
    }
  };

  // === FOCUS FIX IMPLEMENTATION (UPDATED FOR CURSOR POSITION) ===
  const updatePreviewRowField = useCallback((index, field, value) => {
    const inputId = `preview-input-${index}-${field}`;
    const selectId = `preview-select-${index}-${field}`;
    const checkboxId = `preview-checkbox-${index}-is_default`;

    const activeElement = document.activeElement;

    // 1. Capture Focus and Cursor Position
    if (activeElement && (activeElement.id === inputId || activeElement.id === selectId || activeElement.id === checkboxId)) {
      focusRef.current = activeElement.id;
      // ⭐ FIX: Save the current cursor position before re-render
      if (activeElement.type === 'text' || activeElement.type === 'number') {
        cursorPositionRef.current = {
          start: activeElement.selectionStart,
          end: activeElement.selectionEnd,
        };
      }
    } else {
      focusRef.current = null;
      cursorPositionRef.current = { start: 0, end: 0 };
    }

    setImportPreview((prev) => {
      const existingAccountNos = new Set(
        (bankAccounts || [])
          .map((b) => (b.account_number || "").toString().trim())
          .filter(Boolean)
      );

      // 2. Draft the state update
      const draft = prev.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      
      // Special handling for `is_default` to clear others immediately in the draft
      if (field === 'is_default' && value === true) {
        for(let i = 0; i < draft.length; i++){
            if(i !== index) {
                draft[i].is_default = false;
            }
        }
      }

      const revalidated = validateImportRows(draft, existingAccountNos);
      setImportStats(summarizeImport(revalidated));
      return revalidated;
    });

    // 3. Restore focus and Cursor Position
    if (focusRef.current) {
      setTimeout(() => {
        const el = document.getElementById(focusRef.current);
        if (el) {
          el.focus();
          if (el.type === 'text' || el.type === 'number') {
            // ⭐ FIX: Use the captured position instead of always using length
            const { start, end } = cursorPositionRef.current;
            el.setSelectionRange(start, end);
          }
        }
        focusRef.current = null;
        cursorPositionRef.current = { start: 0, end: 0 };
      }, 0);
    }
  }, [bankAccounts]); 
  
  // Checkbox handler needs to be separate due to the 'clear all others' logic
  const handleDefaultChange = useCallback((index, value) => {
    // Manually trigger the field update logic
    updatePreviewRowField(index, "is_default", value);
  }, [updatePreviewRowField]);
  
  // Toggle select handler
  const toggleSelectRow = (index) => {
    setImportPreview((prev) => {
      const next = prev.map((r, i) => {
        if (i !== index) return r;
        if (r.hasError) return r;
        return { ...r, selected: !r.selected };
      });
      setImportStats(summarizeImport(next));
      return next;
    });
  };

  const selectAllValid = () => {
    setImportPreview((prev) => {
      const next = prev.map((r) =>
        r.hasError ? { ...r, selected: false } : { ...r, selected: true }
      );
      setImportStats(summarizeImport(next));
      return next;
    });
  };

  const clearSelection = () => {
    setImportPreview((prev) => {
      const next = prev.map((r) => ({ ...r, selected: false }));
      setImportStats(summarizeImport(next));
      return next;
    });
  };

  const handleConfirmImport = async () => {
    if (importing) return;

    const selected = importPreview.filter((r) => r.selected && !r.hasError);
    if (selected.length === 0) {
      toast.error("No valid rows selected for import.");
      return;
    }

    let defaultIndex = selected.findIndex((r) => r.is_default);
    
    if (defaultIndex === -1 && bankAccounts.length === 0) {
         defaultIndex = 0;
    } else if (defaultIndex === -1 && bankAccounts.length > 0) {
    }


    const sanitizedRows = selected.map((r, i) => ({
      account_name: r.account_name.trim(),
      bank_name: r.bank_name.trim(),
      account_number: r.account_number.trim(),
      ifsc: (r.ifsc || "").trim().toUpperCase(),
      account_code: (r.account_code || "").trim(),
      account_type: (r.account_type || "current").toLowerCase(),
      is_default: i === defaultIndex,
      _rowIndex: r._rowIndex,
    }));

    setImporting(true);

    try {
      const { success = [], failed = [] } = await bulkImportBankAccountsService(
        sanitizedRows
      );

      if (success.length > 0) {
        setBankAccounts((prev) => {
          let updated = [...(prev || [])];

          // Ensure only ONE imported default survives
          let defaultApplied = false;

          const normalized = success.map((acc) => {
            if (acc.is_default && !defaultApplied) {
              defaultApplied = true;
              return acc; // keep true
            }
            return { ...acc, is_default: false };
          });

          return [...updated, ...normalized];
        });

        reloadOnboardingStatus();
        toast.success(`Imported ${success.length} bank account(s).`);
      }

      if (failed.length > 0) {
        setImportPreview((prev) => {
          let next = prev.map((row) => {
            const fail = failed.find((f) => f.rowIndex === row._rowIndex);
            if (!fail) return row;

            const errors = Array.isArray(row.errors) ? [...row.errors] : [];
            if (!errors.includes(fail.message)) errors.push(fail.message);

            return {
              ...row,
              errors,
              hasError: true,
              selected: false,
            };
          });
          
          setImportStats(summarizeImport(next));
          return next;
        });

        toast.error(`${failed.length} row(s) failed. Fix and retry.`);
        return;
      }

      setShowImportPreview(false);
      setImportPreview([]);
      setImportStats({ total: 0, valid: 0, withWarnings: 0, withErrors: 0 });
    } catch (err) {
      console.error(err);
      toast.error("Bulk import failed.");
    } finally {
      setImporting(false);
    }
  };


  // --- CRUD Handlers (unchanged logic) ---
  const openConfirm = ({ title, message, index, type }) => {
    setConfirmData({ title, message, index, type });
    setConfirmOpen(true);
  };
  
  const handleSave = async () => {
    if (!form.account_name || !form.bank_name || !form.account_number) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!validateAccountNumber(form.account_number)) {
      toast.error("Invalid Account Number (must be 9–18 digits).");
      return;
    }

    if (form.ifsc && !validateIFSC(form.ifsc)) {
      toast.error("Invalid IFSC code. Format: ABCD0XXXXXX");
      return;
    }

    try {
      if (editingIndex !== null) {
        const acc = bankAccounts[editingIndex];
        const res = await updateBankAccountService(acc._id, form);

        let updated = [...bankAccounts];

        if (res.bank_account.is_default) {
          updated = updated.map((a) => ({
            ...a,
            is_default: false,
          }));
        }

        updated[editingIndex] = res.bank_account;

        setBankAccounts(updated);
        reloadOnboardingStatus();
        toast.success("Bank account updated!");
      } else {
        const res = await createBankAccountService(form);

        let updated = [...bankAccounts];

        if (res.bank_account.is_default) {
          updated = updated.map((a) => ({
            ...a,
            is_default: false,
          }));
        }

        updated.push(res.bank_account);

        setBankAccounts(updated);
        reloadOnboardingStatus();
        toast.success("Bank account added!");
      }

      setOpen(false);
      setEditingIndex(null);
      setForm(emptyForm);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save bank account");
    }
  };

  const handleEdit = (i) => {
    setEditingIndex(i);
    setForm(bankAccounts[i]);
    setOpen(true);
  };

  const handleDelete = (index) => {
    openConfirm({
      title: "Delete Bank Account",
      message: "Are you sure you want to delete this bank account?",
      index,
      type: "bank",
    });
  };
  
  const handleLoanTypeSubmit = async () => {
    if(!canCreate("settings_loan_configuration")) return;
    if (!loanForm.loan_type) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editingLoanIndex !== null) {
        const acc = companyLoanTypes[editingLoanIndex];
        const res = await updateLoanType(acc._id, loanForm);
        setAllLoanTypes(res.data);
        setEditingLoanIndex(null);
        toast.success(res.message || "Loan type updated successfully!");
        setOpenLoanModal(false);
        setLoanForm((prev) => ({ ...prev, loan_type: "", interest_rate: 0 }));
      } else {
        const res = await addLoanType(loanForm);
        if (res.error) {
          toast.error(res.error);
          setEditingLoanIndex(null);
          setLoanForm((prev) => ({ ...prev, loan_type: "", interest_rate: 0 }));
          return;
        }
        setAllLoanTypes(res.data);
        setEditingLoanIndex(null);
        toast.success(res.message || "Loan type added successfully!");
        setOpenLoanModal(false);
        setLoanForm((prev) => ({ ...prev, loan_type: "", interest_rate: 0 }));
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to save loan type"));
      }
    }
  };

  const handleLoanTypeEdit = (i) => {
    setEditingLoanIndex(i);
    const item = companyLoanTypes[i];

    setLoanForm({
      loan_type: item.loan_type || "",
      interest_rate: item.interest_rate || 0,
    });

    setOpenLoanModal(true);
  };

  const handleLoanTypeDelete = (index) => {
    openConfirm({
      title: "Delete loan type",
      message: "Are you sure you want to delete this loan type?",
      index,
      type: "loan",
    });
  };

  const goToUpgrade = () => {
    navigate("/subscription", {
      state: {
        requiredFeature: "multi_bank_accounts",
        reason: "feature_upgrade_required",
      },
    });
  };

  const resetForm = () => {
    setForm({
      bank_name: "",
      account_number: "",
      ifsc: "",
      account_type: "current",
      is_default: false,
    });
  };

  // =================================================================================
  // RENDER START
  // =================================================================================

  return (
    <>
    {canViewBankConfig && (
      <Card id="setting-bank-accounts">
        {/* Bank Accounts Card Header and Content */}
        <CardHeader>
          <CardTitle className="flex justify-between items-center gap-3">
            <span>Bank Accounts</span>

            <div className="flex items-center gap-2">
              {/* Template */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadBankTemplate}
              >
                <Download className="w-4 h-4 mr-2" />
                Template (.xlsx)
              </Button>

              {/* Import */}
              <input
                ref={importInputRef}
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleBankImport}
              />

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          (!isPaidOnline && !hasFeature("multi_bank_accounts")) || !canCreateBankConfig
                        } // keep clickable for upgrade navigation
                        className={`flex items-center gap-2 ${
                          !hasFeature("multi_bank_accounts")
                            ? "opacity-70 cursor-pointer"
                            : ""
                        }`}
                        onClick={() => {
                          if(!canCreate("settings_bank_configuration", true)) return;
                          if (!hasFeature("multi_bank_accounts")) {
                            goToUpgrade();
                            return;
                          }
                          importInputRef.current?.click();
                        }}
                      >
                        <Upload className="w-4 h-4" />
                        Import (.xlsx)
                        {!hasFeature("multi_bank_accounts") && (
                          <Lock className="w-4 h-4 ml-1" />
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>

                  {!hasFeature("multi_bank_accounts") && (
                    <TooltipContent>
                      <p className="font-semibold">Multiple Bank Accounts</p>
                      <p className="text-xs">
                        Upgrade your plan to add more than one bank account.
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              {/* Add Dialog */}
              <Dialog
                open={open}
                onOpenChange={(isOpen) => {
                  if (!canCreate("settings_bank_configuration", true)) return;
                  setOpen(isOpen);
                  if (!isOpen) {
                    resetForm();
                  }
                }}
              >
                <DialogTrigger asChild>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="sm"
                            disabled={(!isPaidOnline && isBankLimitReached) || !canCreateBankConfig} // keep clickable for upgrade navigation
                            className={`flex items-center gap-2 ${
                              isBankLimitReached
                                ? "opacity-70 cursor-pointer"
                                : ""
                            }`}
                            onClick={(e) => {
                              if (!canCreate("settings_bank_configuration", true))
                                return;
                              if (isBankLimitReached) {
                                e.preventDefault();
                                goToUpgrade();
                                return;
                              }
                              setOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4" />
                            Add Bank Account
                            {isBankLimitReached && (
                              <Lock className="w-4 h-4 ml-1" />
                            )}
                          </Button>
                        </span>
                      </TooltipTrigger>

                      {isBankLimitReached && (
                        <TooltipContent>
                          <p className="font-semibold">
                            Multiple Bank Accounts
                          </p>
                          <p className="text-xs">
                            Upgrade your plan to add more than one bank account.
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </DialogTrigger>
                <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingIndex !== null
                        ? "Edit Bank Account"
                        : "Add Bank Account"}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="grid gap-4 py-4 grid-cols-1 md:grid-cols-2">
                    {/* Bank Name Select */}
                    <div className="space-y-1 md:col-span-1">
                      <Label>Bank Name *</Label>
                      <Select
                        value={
                          BANK_OPTIONS.includes(form.bank_name)
                            ? form.bank_name
                            : ""
                        }
                        onValueChange={(value) => {
                          if (value === "Other") {
                            handleChange("bank_name", "");
                          } else {
                            handleChange("bank_name", value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Bank Name" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANK_OPTIONS.map((bank) => (
                            <SelectItem key={bank} value={bank}>
                              {bank}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Custom Bank Name Input */}
                    {!BANK_OPTIONS.includes(form.bank_name) && (
                      <div className="space-y-1 md:col-span-1">
                        <Label>Custom Bank Name *</Label>
                        <Input
                          value={form.bank_name}
                          onChange={(e) =>
                            handleChange("bank_name", e.target.value)
                          }
                          placeholder="Enter Bank Name"
                        />
                      </div>
                    )}

                    {/* Other Inputs */}
                    <div className="space-y-1">
                      <Label>Bank Account Name *</Label>
                      <Input
                        value={form.account_name}
                        onChange={(e) =>
                          handleChange("account_name", e.target.value)
                        }
                        placeholder="e.g. Element Tree Pvt Ltd"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label>Account Number *</Label>
                      <Input
                        value={form.account_number}
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, "");
                          handleChange("account_number", onlyNumbers);
                        }}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter 9–18 digit account number"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label>IFSC</Label>
                      <Input
                        value={form.ifsc}
                        onChange={(e) =>
                          handleChange("ifsc", e.target.value.toUpperCase())
                        }
                        placeholder="e.g. HDFC0001234"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label>Account Code</Label>
                      <Input
                        value={form.account_code}
                        onChange={(e) =>
                          handleChange("account_code", e.target.value)
                        }
                        placeholder="Optional internal reference code"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label>Account Type</Label>
                      <Select
                        value={form.account_type}
                        onValueChange={(value) =>
                          handleChange("account_type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current">
                            Current Account
                          </SelectItem>
                          <SelectItem value="savings">
                            Savings Account
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2 mt-2 md:col-span-2">
                      <Checkbox
                        checked={form.is_default}
                        onCheckedChange={(checked) =>
                          handleChange("is_default", checked)
                        }
                        id="default_acc"
                      />
                      <Label htmlFor="default_acc">
                        Set as Default Bank Account
                      </Label>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setOpen(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSave}>Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Bank Name</TableHead>
                  <TableHead>Account No.</TableHead>
                  <TableHead>IFSC</TableHead>
                  <TableHead>Account Type</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {bankAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-6 text-gray-500"
                    >
                      No bank accounts found
                    </TableCell>
                  </TableRow>
                ) : (
                  bankAccounts.map((acc, i) => (
                    <TableRow key={i}>
                      <TableCell>{acc.account_name}</TableCell>
                      <TableCell>{acc.bank_name}</TableCell>
                      <TableCell>{acc.account_number}</TableCell>
                      <TableCell>{acc.ifsc}</TableCell>
                      <TableCell className="capitalize">
                        {acc.account_type}
                      </TableCell>
                      <TableCell>{acc.is_default ? "Yes" : "No"}</TableCell>

                      <TableCell className="flex justify-end gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadSpecificBankTemplate(acc.bank_name)}
                                aria-label="Download bank payment template"
                              >
                                <Download className="w-4 h-4 text-emerald-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Download Payment Template</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <Button
                          variant="ghost"
                          size="sm"
                          className={
                            !canEditBankConfig
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }
                          disabled={!canEditBankConfig}
                          onClick={() => {
                            if (!canEdit("settings_bank_configuration", true))
                              return;
                            handleEdit(i);
                          }}
                          aria-label="Edit bank account"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className={
                            !canDeleteBankConfig
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }
                          disabled={!canDeleteBankConfig}
                          onClick={() => {
                            if (!canDelete("settings_bank_configuration", true))
                              return;
                            handleDelete(i);
                          }}
                          aria-label="Delete bank account"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    )}

      {/* LOAN TYPES CARD */}
    {canViewLoanConfig && (
      <Card id="setting-loan-types">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Loan Types</span>

            <Dialog
              open={openLoanModal}
              onOpenChange={(v) => {
                if (!canCreate("settings_loan_configuration", true)) return;
                setOpenLoanModal(v);
                if (!v) {
                  setEditingLoanIndex(null);
                  setLoanForm({ loan_type: "", interest_rate: 0 });
                }
              }}
            >
              <DialogTrigger asChild>
                <Button disabled={!canCreateLoanConfig} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Loan Types
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingLoanIndex !== null
                      ? "Edit Loan Type"
                      : "Add Loan Type"}
                  </DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 py-4 grid-cols-1 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>Loan Type *</Label>
                    <Input
                      value={loanForm.loan_type}
                      onChange={(e) =>
                        setLoanForm((prev) => ({
                          ...prev,
                          loan_type: e.target.value,
                        }))
                      }
                      placeholder="e.g. Personal Loan"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Interest Rate *</Label>
                    <Input
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={loanForm.interest_rate}
                      onChange={(e) => {
                        setLoanForm((prev) => ({
                          ...prev,
                          interest_rate: e.target.value,
                        }));
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Enter interest rate"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setOpenLoanModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleLoanTypeSubmit}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Loan Type</TableHead>
                  <TableHead>Interest Rate</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {companyLoanTypes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center py-6 text-gray-500"
                    >
                      No loan types found
                    </TableCell>
                  </TableRow>
                ) : (
                  companyLoanTypes?.map((acc, i) => (
                    <TableRow key={i}>
                      <TableCell>{acc.loan_type}</TableCell>
                      <TableCell>{acc.interest_rate}%</TableCell>

                      <TableCell className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={
                            !canEditLoanConfig
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }
                          disabled={!canEditLoanConfig}
                          onClick={() => {
                            if (!canEdit("settings_loan_configuration", true))
                              return;
                            handleLoanTypeEdit(i);
                          }}
                          aria-label="Edit loan type"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className={
                            !canDeleteLoanConfig
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }
                          disabled={!canDeleteLoanConfig}
                          onClick={() => {
                            if (!canDelete("settings_loan_configuration", true))
                              return;
                            handleLoanTypeDelete(i);
                          }}
                          aria-label="Delete loan type"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    )}
      {/* =================================================================================
          IMPORT PREVIEW MODAL
          ================================================================================= */}
      <Dialog open={showImportPreview} onOpenChange={setShowImportPreview}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0">
          {/* Header */}
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Upload className="w-5 h-5 text-primary mt-1" />
              <div>
                <DialogTitle>Import Bank Accounts – Preview</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Review and edit the data below. Rows with Errors must be
                  fixed before import.
                </p>
              </div>
            </div>
          </div>

          {/* Summary / Actions Bar */}
          <div className="px-5 py-3 border-b bg-muted/40 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Info className="w-4 h-4" />
                <span>
                  Total:{" "}
                  <span className="font-semibold">{importStats.total}</span>
                </span>
              </div>
              <div
                className={`flex items-center gap-1 ${importStats.valid > 0 ? "text-emerald-600" : "text-muted-foreground"}`}
              >
                <CheckCircle className="w-4 h-4" />
                <span>
                  Valid:{" "}
                  <span className="font-semibold">{importStats.valid}</span>
                </span>
              </div>
              <div
                className={`flex items-center gap-1 ${importStats.withWarnings > 0 ? "text-amber-600" : "text-muted-foreground"}`}
              >
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Warnings:{" "}
                  <span className="font-semibold">
                    {importStats.withWarnings}
                  </span>
                </span>
              </div>
              <div
                className={`flex items-center gap-1 ${importStats.withErrors > 0 ? "text-red-600" : "text-muted-foreground"}`}
              >
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Errors:{" "}
                  <span className="font-semibold">
                    {importStats.withErrors}
                  </span>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAllValid}>
                Select all valid
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear selection
              </Button>
            </div>
          </div>

          {/* Table Container - Uses flex-1 and overflow-y-auto */}
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background shadow-sm z-20 border-b">
                <tr className="text-left font-semibold text-muted-foreground [&>th]:whitespace-nowrap">
                  <th className="px-3 py-3 w-[5%]">Sel</th>
                  <th className="px-3 py-3 w-[5%]">Row</th>
                  <th className="px-3 py-3 w-[18%]">Account Name *</th>
                  <th className="px-3 py-3 w-[15%]">Bank Name *</th>
                  <th className="px-3 py-3 w-[15%]">Account No *</th>
                  <th className="px-3 py-3 w-[10%]">IFSC</th>
                  <th className="px-3 py-3 w-[10%]">Account Code</th>
                  <th className="px-3 py-3 w-[10%]">Type *</th>
                  <th className="px-3 py-3 w-[7%]">Default</th>
                  <th className="px-3 py-3 w-[10%]">Status</th>
                </tr>
              </thead>
              <tbody>
                {importPreview.map((row, idx) => {
                  const statusColor = row.hasError
                    ? "border-red-500 hover:bg-red-50/20"
                    : row.hasWarning
                      ? "border-amber-400 hover:bg-amber-50/20"
                      : "border-emerald-500 hover:bg-emerald-50/20";

                  // Status Indicator: Wraps text and aligns icon to top
                  const StatusIndicator = row.hasError ? (
                    <span className="text-red-600 flex items-start gap-1 whitespace-normal break-words">
                      <AlertTriangle className="w-3 h-3 min-w-[0.75rem] mt-1" />
                      <span className="" title={row.errors?.[0] || "Error"}>
                        {row.errors?.[0] || "Error"}
                      </span>
                    </span>
                  ) : row.hasWarning ? (
                    <span className="text-amber-600 flex items-start gap-1 whitespace-normal break-words">
                      <AlertTriangle className="w-3 h-3 min-w-[0.75rem] mt-1" />
                      <span className="" title={row.warnings?.[0] || "Warning"}>
                        {row.warnings?.[0] || "Warning"}
                      </span>
                    </span>
                  ) : (
                    <span className="text-emerald-600 flex items-center gap-1 whitespace-nowrap">
                      <CheckCircle className="w-3 h-3 min-w-[0.75rem]" />
                      <span>OK</span>
                    </span>
                  );

                  return (
                    <tr
                      key={idx}
                      className={`border-b transition-colors ${statusColor} border-l-4 align-top`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          id={`preview-checkbox-${idx}-selected`}
                          className="h-4 w-4 accent-primary mt-1"
                          checked={!!row.selected}
                          disabled={row.hasError}
                          onCheckedChange={() => toggleSelectRow(idx)}
                        />
                      </td>
                      {/* Row number uses align-top */}
                      <td className="px-3 py-2 text-muted-foreground font-mono align-top">
                        #{row._rowIndex}
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellInput
                          id={`preview-input-${idx}-account_name`}
                          value={row.account_name}
                          onChange={(e) =>
                            updatePreviewRowField(
                              idx,
                              "account_name",
                              e.target.value,
                            )
                          }
                          error={
                            row.fieldErrors?.account_name === "required"
                              ? "Required"
                              : undefined
                          }
                          placeholder="Account Name"
                        />
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellInput
                          id={`preview-input-${idx}-bank_name`}
                          value={row.bank_name}
                          onChange={(e) =>
                            updatePreviewRowField(
                              idx,
                              "bank_name",
                              e.target.value,
                            )
                          }
                          error={
                            row.fieldErrors?.bank_name === "required"
                              ? "Required"
                              : undefined
                          }
                          placeholder="Bank Name"
                        />
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellInput
                          id={`preview-input-${idx}-account_number`}
                          value={row.account_number}
                          onChange={(e) =>
                            updatePreviewRowField(
                              idx,
                              "account_number",
                              e.target.value.replace(/\D/g, ""),
                            )
                          }
                          error={
                            row.fieldErrors?.account_number === "required"
                              ? "Required"
                              : row.fieldErrors?.account_number === "invalid"
                                ? "9–18 digits (unique)"
                                : undefined
                          }
                          placeholder="Account Number"
                        />
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellInput
                          id={`preview-input-${idx}-ifsc`}
                          value={row.ifsc}
                          onChange={(e) =>
                            updatePreviewRowField(
                              idx,
                              "ifsc",
                              e.target.value.toUpperCase(),
                            )
                          }
                          error={
                            row.fieldErrors?.ifsc === "invalid"
                              ? "Invalid IFSC"
                              : undefined
                          }
                          placeholder="IFSC"
                        />
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellInput
                          id={`preview-input-${idx}-account_code`}
                          value={row.account_code}
                          onChange={(e) =>
                            updatePreviewRowField(
                              idx,
                              "account_code",
                              e.target.value,
                            )
                          }
                          warning={
                            row.fieldErrors?.account_code === "warning"
                              ? "Looks long"
                              : undefined
                          }
                          placeholder="Code"
                        />
                      </td>

                      <td className="px-3 py-2">
                        <PreviewCellSelect
                          id={`preview-select-${idx}-account_type`}
                          value={row.account_type}
                          onChange={(value) =>
                            updatePreviewRowField(idx, "account_type", value)
                          }
                          error={
                            row.fieldErrors?.account_type === "invalid"
                              ? "Use current/savings"
                              : undefined
                          }
                          options={[
                            { value: "current", label: "Current" },
                            { value: "savings", label: "Savings" },
                          ]}
                        />
                      </td>

                      <td className="px-3 py-2">
                        <Checkbox
                          id={`preview-checkbox-${idx}-is_default`}
                          className="h-4 w-4 accent-primary mt-2"
                          checked={!!row.is_default}
                          disabled={row.hasError}
                          onCheckedChange={(checked) =>
                            handleDefaultChange(idx, checked)
                          }
                        />
                      </td>

                      <td className="px-3 py-2 align-top">{StatusIndicator}</td>
                    </tr>
                  );
                })}

                {importPreview.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No rows to preview. Please upload a valid XLSX file.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t bg-background flex rounded-b-xl items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              <b>Bulk Import Rules:</b> Only <b>Valid (Green/Yellow)</b> rows
              are selected for import. All `is_default` settings will be
              collapsed to <b>one</b> default account among existing and
              imported accounts.
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowImportPreview(false)}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={
                  importing ||
                  importPreview.filter((r) => r.selected && !r.hasError)
                    .length === 0
                }
              >
                {importing
                  ? "Importing..."
                  : `Import ${importPreview.filter((r) => r.selected && !r.hasError).length} Selected`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={async () => {
          const { index, type } = confirmData;
          try {
            if (type === "bank") {
              const acc = bankAccounts[index];
              await deleteBankAccountService(acc._id);
              setBankAccounts(bankAccounts.filter((_, idx) => idx !== index));
              toast.success("Bank account deleted");
            }

            if (type === "loan") {
              const acc = companyLoanTypes[index];
              const res = await deleteLoanType(acc._id);
              setAllLoanTypes(Array.isArray(res.data) ? res.data : []);
              setEditingLoanIndex(null);
              toast.success(res.message || "Loan type deleted");
            }
          } catch (err) {
            toast.error("Failed to delete");
          }
        }}
      />
    </>
  );
};

export default BankAccountsTab;
